// new code

import React, { useEffect, useCallback, useRef } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Divider,
  List,
  ListItem,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Modal,
  Fab,
  Badge,
} from '@mui/material';
import {
  Add,
  Remove,
  Search,
  Delete,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowUp,
  KeyboardArrowDown,
  ShoppingCart,
  Close,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  setProducts,
  setFiltered,
  setCategory,
  setSearchTerm,
  setVisibleCount,
  setLoading,
  setScrollLoading,
  setCategories,
  setScrollState,
  setScrollButtons,
} from '../../store/productSlice';
import { addToCart, setCartItems, updateQuantity, removeItem, setManualQuantity } from '../../store/cartSlice';
import { toast } from 'react-toastify';
import photo from '../../assets/photo.png';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddProduct from '../Products/AddProduct';
import Color from '../../components/Color';
import { getAllProducts } from '../../service/productService';

const CreateOrder = () => {
  const dispatch = useDispatch();
  const {
    products,
    filtered,
    category,
    searchTerm,
    visibleCount,
    hasMore,
    loading,
    scrollLoading,
    categories,
    isAtStart,
    isAtEnd,
    showScrollUp,
    showScrollDown,
  } = useSelector((state) => state.products);
  const { cartItems, totals, totalItems } = useSelector((state) => state.cart);
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const scrollRef = useRef(null);
  const categoryRef = useRef(null);
  const navigate = useNavigate();
  const [openModal, setOpenModal] = React.useState(false);
  const [openCartModal, setOpenCartModal] = React.useState(false);

  const fetchAllProducts = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      let all = [], page = 1, last = 1;
      do {
        const res = await getAllProducts(page);
        const data = res.data.data;
        if (res.data.status !== 'success' || !Array.isArray(data.data)) {
          toast.error('Unexpected response from server');
          break;
        }
        all = all.concat(data.data);
        last = data.last_page;
        page++;
      } while (page <= last);
      dispatch(setProducts(all));
    } catch (err) {
      toast.error('Failed to fetch products');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  useEffect(() => {
    let filteredList = products.filter((p) => p.category?.name !== 'Cake');
    if (searchTerm) {
      filteredList = filteredList.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (category !== 'All') {
      filteredList = filteredList.filter((p) => p.category?.name === category);
    }
    dispatch(setFiltered(filteredList));
  }, [searchTerm, category, products, dispatch]);

  useEffect(() => {
    const stored = localStorage.getItem('selectedProducts');
    console.log('CreateOrder: Stored Products from localStorage:', stored); // Debug log
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          dispatch(setCartItems(parsed));
        } else {
          console.warn('CreateOrder: Parsed products is not an array:', parsed);
          dispatch(setCartItems([]));
        }
      } catch (error) {
        console.error('CreateOrder: Error parsing selectedProducts:', error);
        dispatch(setCartItems([]));
        toast.error('Error loading cart items');
      }
    } else {
      console.warn('CreateOrder: No selectedProducts in localStorage');
      dispatch(setCartItems([]));
    }
  }, [dispatch]);

  useEffect(() => {
    const categoryCounts = products.reduce((acc, p) => {
      const catName = p.category?.name || 'Uncategorized';
      if (catName !== 'Cake') {
        acc[catName] = (acc[catName] || 0) + 1;
      }
      return acc;
    }, {});
    dispatch(setCategories(['All', ...Object.entries(categoryCounts).map(([name, count]) => `${name} (${count})`)]));
  }, [products, dispatch]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || !hasMore) return;
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
      dispatch(setScrollLoading(true));
      setTimeout(() => {
        dispatch(setVisibleCount(visibleCount + 20));
        dispatch(setScrollLoading(false));
      }, 500);
    }
  }, [hasMore, visibleCount, dispatch]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleScrollButtonClick = useCallback((direction) => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollAmount = direction === 'up' ? -300 : 300;
    container.scrollBy({ top: scrollAmount, behavior: 'smooth' });
  }, []);

  const updateScrollButtons = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    dispatch(setScrollButtons({
      showScrollUp: container.scrollTop > 50,
      showScrollDown: container.scrollTop < container.scrollHeight - container.clientHeight - 50,
    }));
  }, [dispatch]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleFullScroll = () => {
      handleScroll();
      updateScrollButtons();
    };
    container.addEventListener('scroll', handleFullScroll);
    updateScrollButtons();
    return () => container.removeEventListener('scroll', handleFullScroll);
  }, [handleScroll, updateScrollButtons]);

  const handleCategoryScroll = useCallback((direction) => {
    const container = categoryRef.current;
    if (!container) return;
    const scrollAmount = direction === 'left' ? -150 : 150;
    const maxScroll = container.scrollWidth - container.clientWidth;

    if (direction === 'right' && container.scrollLeft >= maxScroll - 1) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
      dispatch(setScrollState({ isAtStart: true, isAtEnd: false }));
    } else if (direction === 'left' && container.scrollLeft <= 0) {
      container.scrollTo({ left: maxScroll, behavior: 'smooth' });
      dispatch(setScrollState({ isAtStart: false, isAtEnd: true }));
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(() => {
        const newScrollLeft = container.scrollLeft;
        dispatch(setScrollState({
          isAtStart: newScrollLeft <= 0,
          isAtEnd: newScrollLeft >= maxScroll - 1,
        }));
      }, 300);
    }
  }, [dispatch]);

  useEffect(() => {
    const container = categoryRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      dispatch(setScrollState({
        isAtStart: container.scrollLeft <= 0,
        isAtEnd: container.scrollLeft >= maxScroll - 1,
      }));
    };

    container.addEventListener('scroll', updateScrollState);
    updateScrollState();
    return () => container.removeEventListener('scroll', updateScrollState);
  }, [categories.length, dispatch]);

  const handleAddToCart = (product) => {
    const cartItem = {
      id: product.id,
      product_id: product.id, // Ensure product_id is included
      name: product.name,
      base_price: parseFloat(product.base_price),
      tax_rate: parseFloat(product.tax_rate || 0),
      image_url: product.image_url,
      quantity: 1,
    };
    console.log('CreateOrder: Adding to cart:', cartItem); // Debug log
    dispatch(addToCart(cartItem));
    toast.success(`${product.name} added to cart!`);
  };

  const renderCartDetails = () => (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: 2,
        maxHeight: isMobileOrTablet ? '80vh' : 'none',
        // height: isMobileOrTablet ? 'auto' : '765px',
                height: {xs:'auto', sm:'auto', md:'460px', lg:'765px'},

        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'white',
          p: { xs: 1, sm: 2 },
          borderBottom: '1px solid #eee',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontSize={{ xs: '16px', sm: '18px' }}>
            Order Details
          </Typography>
          {isMobileOrTablet && (
            <IconButton onClick={() => setOpenCartModal(false)}>
              <Close />
            </IconButton>
          )}
        </Box>
      </Box>
      <List
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: { xs: 1, sm: 2 },
        }}
      >
        {cartItems.map((item) => {
          const price = parseFloat(item.base_price);
          const gst = parseFloat(item.tax_rate);
          const unitTotal = price + (price * gst) / 100;
          const total = unitTotal * item.quantity;
          return (
            <ListItem
              key={item.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                width: '100%',
                borderBottom: '1px solid #eee',
                pb: 1,
                px: { xs: 0.5, sm: 0 },
              }}
            >
              <Box display="flex" alignItems="center" width="100%" gap={1}>
                <Box
                  component="img"
                  src={item.image_url || photo}
                  alt={item.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = photo;
                  }}
                  sx={{
                    width: { xs: 40, sm: 50, },
                    height: { xs: 40, sm: 50 },
                    borderRadius: 1,
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '12px', sm: '14px' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '10px', sm: '11px' } }}
                  >
                    ₹{unitTotal.toFixed(2)} × {item.quantity}
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                mt={1}
                flexWrap="wrap"
                gap={1}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => dispatch(updateQuantity({ id: item.id, change: -1 }))}
                    disabled={item.quantity <= 1}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                  <TextField
                    value={item.quantity}
                    onChange={(e) => dispatch(setManualQuantity({ id: item.id, quantity: parseInt(e.target.value) || 1 }))}
                    type="number"
                    size="small"
                    inputProps={{
                      min: 1,
                      style: { width: '40px', textAlign: 'center', fontSize: '12px' },
                      inputMode: 'numeric',
                    }}
                    sx={{
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '& input[type=number]': { MozAppearance: 'textfield' },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => dispatch(updateQuantity({ id: item.id, change: 1 }))}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <IconButton size="small" color="error" onClick={() => dispatch(removeItem(item.id))}>
                    <Delete fontSize="small" />
                  </IconButton>
                  <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight="bold">
                    ₹{total.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          );
        })}
        {cartItems.length === 0 && (
          <Typography
            variant="body2"
            color="error"
            textAlign="center"
            mt={2}
            fontSize={{ xs: '12px', sm: '14px' }}
          >
            No items in cart.
          </Typography>
        )}
      </List>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          bgcolor: 'white',
          p: { xs: 1, sm: 2 },
          borderTop: '1px solid #eee',
        }}
      >
        {/* <Divider sx={{ mb: 2 }} /> */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
        >
          Total:
        </Typography>
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ fontSize: { xs: '16px', sm: '18px' } }}
        >
          ₹{totals.total.toFixed(2)}
        </Typography>
        <Button
          fullWidth
          sx={{
            mt: 1,
            background: Color.primary.main,
            color: 'white',
            fontSize: { xs: '12px', sm: '14px' },
            py: 1,
            '&:disabled': {
              background: '#7e7a7aff',
              color: '#f3f0f0ff',
              cursor: 'not-allowed',
              pointerEvents: 'auto',
            },
          }}
          onClick={() => {
            setOpenCartModal(false);
            navigate('/order-details');
          }}
          disabled={cartItems.length === 0}
        >
          Proceed Order
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      p={{ xs: 0, sm: 0 }}
      mt={{ xs: 2 }}
      mb={{ xs: 0, sm: 'auto' }}
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      gap={{ xs: 1, sm: 2 }}
      sx={{ fontFamily: '"Poppins", sans-serif' }}
    >
      <Box
        flex={1}
        sx={{ width: { xs: '100%', md: '70%' } }}
        position="relative"
      >
        <Box
          sx={{
            position: 'sticky',
            top: { xs: 0, md: 17 },
            zIndex: 10,
            backgroundColor: 'white',
            borderRadius: '6px',
            width: '99%',
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 2 }}
            alignItems="center"
            sx={{ width: '100%' }}
            p={{ xs: 1, md: 2 }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => dispatch(setSearchTerm(e.target.value))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{
                flex: 1,
                width: { xs: '100%', sm: '250px' },
              }}
            />
            <Button
              sx={{
                flexShrink: 0,
                minWidth: { xs: '100%', sm: '150px', md: '180px' },
                background: Color.primary.main,
                color: 'white',
                fontSize: { xs: '12px', sm: '14px' },
              }}
              startIcon={<AddIcon />}
              onClick={() => setOpenModal(true)}
            >
              Add Product
            </Button>
          </Box>
          <Paper
            sx={{
              height: { xs: 48, sm: 56, md: 60 },
              mt: { xs: 1, sm: 0 },
              mb: { xs: 1, sm: 4 },
              px: { xs: 1, sm: 1.5 },
              backgroundColor: '#f9f9f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              overflow: 'hidden',
              borderRadius: 2,
            }}
            elevation={2}
          >
            <IconButton
              onClick={() => handleCategoryScroll('left')}
              disabled={isAtStart || categories.length <= 1}
              sx={{
                flexShrink: 0,
                p: { xs: 0.5, sm: 1 },
                color: Color.primary.main,
                '&:disabled': { color: '#ccc' },
                border: '1px solid gray',
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Box
              ref={categoryRef}
              sx={{
                display: 'flex',
                gap: { xs: 0.8, sm: 1.2, md: 1.2 },
                pl: 1,
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                flexGrow: 1,
                py: 0.5,
                boxShadow: 'none',
                scrollBehavior: 'smooth',
                ':hover': 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
              }}
            >
              {categories.map((cat) => {
                const catName = cat.split(' (')[0];
                return (
                  <Button
                    key={cat}
                    variant={category === catName ? 'contained' : 'outlined'}
                    onClick={() => dispatch(setCategory(catName))}
                    sx={{
                      flexShrink: 0,
                      borderRadius: '20px',
                      textTransform: 'none',
                      px: { xs: 0.8, sm: 1, md: 1.2 },
                      py: { xs: 0.4, sm: 0.5 },
                      fontSize: { xs: '10px', sm: '11px', md: '12px' },
                      fontWeight: 500,
                      minWidth: { xs: 'auto', sm: '70px', md: '80px' },
                      background: category === catName ? Color.primary.main : 'white',
                      color: category === catName ? 'white' : 'black',
                      borderColor: Color.primary.main,
                      boxShadow: 'none',
                      '&:hover': {
                        background: category === catName ? Color.primary.dark : '#f1f1f1',
                      },
                    }}
                  >
                    {cat}
                  </Button>
                );
              })}
            </Box>
            <IconButton
              onClick={() => handleCategoryScroll('right')}
              disabled={isAtEnd || categories.length <= 1}
              sx={{
                flexShrink: 0,
                p: { xs: 0.5, sm: 1 },
                color: Color.primary.main,
                '&:disabled': { color: '#ccc' },
                border: '1px solid gray',
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Paper>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              ref={scrollRef}
              sx={{
                height: { xs: '60vh', sm: '70vh', md: '80vh' },
                overflowY: 'auto',
                pr: { xs: 0.5, sm: 1 },
              }}
            >
              <Grid
                container
                spacing={{ xs: 1, sm: 2 }}
                justifyContent="flex-start"
                sx={{
                  display: { xs: 'grid', sm: 'flex' },
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'none' },
                  gap: { xs: '8px', sm: 2, md: 2 },
                }}
              >
                {filtered.slice(0, visibleCount).map((p) => {
                  const price = parseFloat(p.base_price);
                  const gst = parseFloat(p.tax_rate);
                  const total = price + (price * gst) / 100;
                  return (
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      md={4}
                      lg={3}
                      key={p.id}
                      display="flex"
                      justifyContent="flex-start"
                      mt={{ xs: 1, md: 0, lg:1 }}
                    >
                      <Card
                        onClick={() => handleAddToCart(p)}
                        sx={{
                          width: { xs: '100%', sm: 150.7, md: 148, lg:181.7 },
                          height: { xs: 'auto', sm: 220, md: 225, lg:230 },
                          borderRadius: 2,
                          boxShadow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-start',
                          p: 1,
                          cursor: 'pointer',
                        }}
                      >
                        <img
                          src={p.image_url || photo}
                          alt={p.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = photo;
                          }}
                          style={{
                            width: '100%',
                            height: '80%',
                            borderRadius: 10,
                            objectFit: 'cover',
                            marginBottom: 0,
                            aspectRatio: { xs: '1/1', sm: 'auto' },
                          }}
                        />
                        <CardContent
                          sx={{
                            pl: 0.5,
                            overflow: 'hidden',
                            whiteSpace: 'normal',
                            textOverflow: 'ellipsis',
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={400}
                            sx={{
                              fontSize: { xs: '11px', sm: '12px', md: '12px', lg:'13px' },
                              lineHeight: 1.2,
                              color: 'black',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word',
                            }}
                            title={p.name}
                          >
                            {p.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: { xs: '10px', sm: '12px' } }}
                          >
                            ₹{total.toFixed(2)} <span style={{ color: 'gray' }}>per piece</span>
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {scrollLoading && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!hasMore && !scrollLoading && (
                <Typography textAlign="center" py={2} color="text.secondary" fontWeight={700}>
                  No more products to load
                </Typography>
              )}
            </Box>
            {isMobileOrTablet && (
              <Box
                sx={{
                  position: 'fixed',
                  bottom: 150,
                  right: 15,
                  zIndex: 5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <IconButton
                  onClick={() => handleScrollButtonClick('up')}
                  disabled={!showScrollUp}
                  sx={{
                    bgcolor: 'white',
                    boxShadow: 2,
                    color: Color.primary.main,
                    '&:hover': { bgcolor: 'grey.100' },
                    '&:disabled': { bgcolor: 'grey.200', color: 'grey.500' },
                  }}
                  size="small"
                >
                  <KeyboardArrowUp />
                </IconButton>
                <IconButton
                  onClick={() => handleScrollButtonClick('down')}
                  disabled={!showScrollDown}
                  sx={{
                    bgcolor: 'white',
                    boxShadow: 2,
                    color: Color.primary.main,
                    '&:hover': { bgcolor: 'grey.100' },
                    '&:disabled': { bgcolor: 'grey.200', color: 'grey.500' },
                  }}
                  size="small"
                >
                  <KeyboardArrowDown />
                </IconButton>
              </Box>
            )}
            {isMobileOrTablet && (
              <Fab
                color="primary"
                onClick={() => setOpenCartModal(true)}
                sx={{
                  position: 'fixed',
                  bottom: 80,
                  right: 20,
                  zIndex: 10,
                  background: Color.primary.main,
                  '&:hover': { background: Color.primary.main },
                  '&:active': { background: Color.primary.main },
                  '&:focus': { background: Color.primary.main },
                }}
              >
                <Badge badgeContent={totalItems} color="secondary" size="medium" width={'40px'}>
                  <ShoppingCart />
                </Badge>
              </Fab>
            )}
          </>
        )}
      </Box>
      {isMobileOrTablet && (
        <Modal
          open={openCartModal}
          onClose={() => setOpenCartModal(false)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            sx={{
              width: { xs: '90%', sm: '80%' },
              maxWidth: 400,
              bgcolor: 'white',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {renderCartDetails()}
          </Box>
        </Modal>
      )}
      {!isMobileOrTablet && (
        <Box
          width={{ xs: '100%', sm: '100%', md: '25%', lg: '25%' }}
          minWidth={{ xs: 'auto', md: '300px' }}
          height={{lg:"765px"}}
          sx={{
            position: { md: 'sticky', xs: 'static' },
            top: { md: 15, lg: 17 },
          }}
        >
          {renderCartDetails()}
        </Box>
      )}
      <AddProduct open={openModal} onClose={() => setOpenModal(false)} />
    </Box>
  );
};

export default CreateOrder;

//old code
// import React, { useState, useEffect, useCallback, useRef } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   CircularProgress,
//   Box,
//   Divider,
//   List,
//   ListItem,
//   IconButton,
//   TextField,
//   InputAdornment,
//   Button,
//   Paper,
//   useTheme,
//   useMediaQuery,
//   Modal,
//   Fab,
//   Badge,
// } from "@mui/material";
// import {
//   Add,
//   Remove,
//   Search,
//   Delete,
//   ChevronLeft,
//   ChevronRight,
//   KeyboardArrowUp,
//   KeyboardArrowDown,
//   ShoppingCart,
//   Close,
// } from "@mui/icons-material";
// import { getAllProducts } from "../../service/productService";
// import { toast } from "react-toastify";
// import photo from "../../assets/photo.png";
// import { useNavigate } from "react-router-dom";
// import AddIcon from "@mui/icons-material/Add";
// import AddProduct from "../Products/AddProduct";
// import Color from "../../components/Color";

// const CreateOrder = () => {
//   const [products, setProducts] = useState([]);
//   const [filtered, setFiltered] = useState([]);
//   const [cartItems, setCartItems] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [category, setCategory] = useState("All");
//   const [openModal, setOpenModal] = useState(false);
//   const [visibleCount, setVisibleCount] = useState(20);
//   const [scrollLoading, setScrollLoading] = useState(false);
//   const [hasMore, setHasMore] = useState(true);
//   const [isAtStart, setIsAtStart] = useState(true);
//   const [isAtEnd, setIsAtEnd] = useState(false);
//   const [showScrollUp, setShowScrollUp] = useState(false);
//   const [showScrollDown, setShowScrollDown] = useState(false);
//   const [openCartModal, setOpenCartModal] = useState(false);

//   const theme = useTheme();
//   const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
//   const scrollRef = useRef(null);
//   const categoryRef = useRef(null);
//   const navigate = useNavigate();

//   const fetchAllProducts = useCallback(async () => {
//     setLoading(true);
//     try {
//       let all = [],
//         page = 1,
//         last = 1;
//       do {
//         const res = await getAllProducts(page);
//         const data = res.data.data;
//         if (res.data.status !== "success" || !Array.isArray(data.data)) {
//           toast.error("Unexpected response from server");
//           break;
//         }
//         all = all.concat(data.data);
//         last = data.last_page;
//         page++;
//       } while (page <= last);
//       setProducts(all);
//       setFiltered(all);
//     } catch (err) {
//       toast.error("Failed to fetch products");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     fetchAllProducts();
//   }, [fetchAllProducts]);

//   useEffect(() => {
//     let filteredList = products.filter((p) => p.category?.name !== "Cake");
//     if (searchTerm) {
//       filteredList = filteredList.filter((p) =>
//         p.name.toLowerCase().includes(searchTerm.toLowerCase())
//       );
//     }
//     if (category !== "All") {
//       filteredList = filteredList.filter((p) => p.category?.name === category);
//     }
//     setFiltered(filteredList);
//     setVisibleCount(20);
//     setHasMore(filteredList.length > 20);
//   }, [searchTerm, category, products]);

//   useEffect(() => {
//     const stored = localStorage.getItem("selectedProducts");
//     if (stored) setCartItems(JSON.parse(stored));
//   }, []);

//   const handleScroll = useCallback(() => {
//     const container = scrollRef.current;
//     if (!container || !hasMore) return;
//     if (
//       container.scrollTop + container.clientHeight >=
//       container.scrollHeight - 50
//     ) {
//       setScrollLoading(true);
//       setTimeout(() => {
//         setVisibleCount((prev) => {
//           const newCount = prev + 20;
//           if (newCount >= filtered.length) setHasMore(false);
//           return newCount;
//         });
//         setScrollLoading(false);
//       }, 500);
//     }
//   }, [hasMore, filtered.length]);

//   useEffect(() => {
//     const container = scrollRef.current;
//     if (!container) return;
//     container.addEventListener("scroll", handleScroll);
//     return () => container.removeEventListener("scroll", handleScroll);
//   }, [handleScroll]);

//   const handleScrollButtonClick = useCallback((direction) => {
//     const container = scrollRef.current;
//     if (!container) return;
//     const scrollAmount = direction === "up" ? -300 : 300;
//     container.scrollBy({ top: scrollAmount, behavior: "smooth" });
//   }, []);

//   const updateScrollButtons = useCallback(() => {
//     const container = scrollRef.current;
//     if (!container) return;
//     setShowScrollUp(container.scrollTop > 50);
//     setShowScrollDown(
//       container.scrollTop < container.scrollHeight - container.clientHeight - 50
//     );
//   }, []);

//   useEffect(() => {
//     const container = scrollRef.current;
//     if (!container) return;
//     const handleFullScroll = () => {
//       handleScroll();
//       updateScrollButtons();
//     };
//     container.addEventListener("scroll", handleFullScroll);
//     updateScrollButtons();
//     return () => container.removeEventListener("scroll", handleFullScroll);
//   }, [handleScroll, updateScrollButtons]);

//   const addToCart = (product) => {
//     setCartItems((prev) => {
//       const exist = prev.find((i) => i.id === product.id);
//       let updated;
//       if (exist) {
//         updated = prev.map((i) =>
//           i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
//         );
//       } else {
//         updated = [...prev, { ...product, quantity: 1 }];
//       }
//       localStorage.setItem("selectedProducts", JSON.stringify(updated));
//       return updated;
//     });
//   };

//   const handleManualQuantityChange = (id, value) => {
//     const quantity = parseInt(value);
//     if (isNaN(quantity) || quantity < 1) return;
//     setCartItems((prev) => {
//       const updated = prev.map((item) =>
//         item.id === id ? { ...item, quantity } : item
//       );
//       localStorage.setItem("selectedProducts", JSON.stringify(updated));
//       return updated;
//     });
//   };

//   const updateQuantity = (id, change) => {
//     setCartItems((prev) => {
//       const updated = prev
//         .map((i) =>
//           i.id === id ? { ...i, quantity: Math.max(1, i.quantity + change) } : i
//         )
//         .filter((i) => i.quantity > 0);
//       localStorage.setItem("selectedProducts", JSON.stringify(updated));
//       return updated;
//     });
//   };

//   const removeItem = (id) => {
//     const updated = cartItems.filter((item) => item.id !== id);
//     setCartItems(updated);
//     localStorage.setItem("selectedProducts", JSON.stringify(updated));
//   };

//   const totals = cartItems.reduce(
//     (acc, item) => {
//       const price = parseFloat(item.base_price);
//       const gstRate = parseFloat(item.tax_rate);
//       const gstAmtPerItem = (price * gstRate) / 100;
//       const totalPerItem = price + gstAmtPerItem;
//       acc.gst += gstAmtPerItem * item.quantity;
//       acc.total += totalPerItem * item.quantity;
//       return acc;
//     },
//     { total: 0, gst: 0 }
//   );

//   const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

//   const categoryCounts = products.reduce((acc, p) => {
//     const catName = p.category?.name || "Uncategorized";
//     if (catName !== "Cake") {
//       acc[catName] = (acc[catName] || 0) + 1;
//     }
//     return acc;
//   }, {});

//   const uniqueCategories = [
//     "All",
//     ...Object.entries(categoryCounts).map(([name, count]) => `${name} (${count})`),
//   ];

//   const handleCategoryScroll = useCallback((direction) => {
//     const container = categoryRef.current;
//     if (!container) return;
//     const scrollAmount = direction === "left" ? -150 : 150;
//     const maxScroll = container.scrollWidth - container.clientWidth;

//     if (direction === "right" && container.scrollLeft >= maxScroll - 1) {
//       container.scrollTo({ left: 0, behavior: "smooth" });
//       setIsAtStart(true);
//       setIsAtEnd(false);
//     } else if (direction === "left" && container.scrollLeft <= 0) {
//       container.scrollTo({ left: maxScroll, behavior: "smooth" });
//       setIsAtStart(false);
//       setIsAtEnd(true);
//     } else {
//       container.scrollBy({ left: scrollAmount, behavior: "smooth" });
//       setTimeout(() => {
//         const newScrollLeft = container.scrollLeft;
//         setIsAtStart(newScrollLeft <= 0);
//         setIsAtEnd(newScrollLeft >= maxScroll - 1);
//       }, 300);
//     }
//   }, []);

//   useEffect(() => {
//     const container = categoryRef.current;
//     if (!container) return;

//     const updateScrollState = () => {
//       const maxScroll = container.scrollWidth - container.clientWidth;
//       setIsAtStart(container.scrollLeft <= 0);
//       setIsAtEnd(container.scrollLeft >= maxScroll - 1);
//     };

//     container.addEventListener("scroll", updateScrollState);
//     updateScrollState();
//     return () => container.removeEventListener("scroll", updateScrollState);
//   }, [uniqueCategories.length]);

//   // Render Cart Details (used in both modal and desktop panel)
//   const renderCartDetails = () => (
//     <Box
//       display="flex"
//       flexDirection="column"
//       sx={{
//         width: "100%",
//         bgcolor: "white",
//         borderRadius: 2,
//         maxHeight: isMobileOrTablet ? "80vh" : "none",
//         // height: isMobileOrTablet ? "auto" : "765px",
//          height: {xs:'auto', sm:'auto', md:'460px', lg:'765px'},
//         overflow: "hidden",
//       }}
//     >
//       {/* Fixed Header */}
//       <Box
//         sx={{
//           position: "sticky",
//           top: 0,
//           zIndex: 1,
//           bgcolor: "white",
//           p: { xs: 1, sm: 2 },
//           borderBottom: "1px solid #eee",
//         }}
//       >
//         <Box display="flex" justifyContent="space-between" alignItems="center">
//           <Typography variant="h6" fontSize={{ xs: "16px", sm: "18px" }}>
//             Order Details
//           </Typography>
//           {isMobileOrTablet && (
//             <IconButton onClick={() => setOpenCartModal(false)}>
//               <Close />
//             </IconButton>
//           )}
//         </Box>
//       </Box>

//       {/* Scrollable Items */}
//       <List
//         sx={{
//           flexGrow: 1,
//           overflowY: "auto",
//           overflowX: "hidden",
//           p: { xs: 1, sm: 2 },
//         }}
//       >
//         {cartItems.map((item) => {
//           const price = parseFloat(item.base_price);
//           const gst = parseFloat(item.tax_rate);
//           const unitTotal = price + (price * gst) / 100;
//           const total = unitTotal * item.quantity;
//           return (
//             <ListItem
//               key={item.id}
//               sx={{
//                 display: "flex",
//                 flexDirection: "column",
//                 alignItems: "flex-start",
//                 gap: 0.5,
//                 width: "100%",
//                 borderBottom: "1px solid #eee",
//                 pb: 1,
//                 px: { xs: 0.5, sm: 0 },
//               }}
//             >
//               <Box display="flex" alignItems="center" width="100%" gap={1}>
//                 <Box
//                   component="img"
//                   src={item.image_url || photo}
//                   alt={item.name}
//                   onError={(e) => {
//                     e.target.onerror = null;
//                     e.target.src = photo;
//                   }}
//                   sx={{
//                     width: { xs: 40, sm: 50 },
//                     height: { xs: 40, sm: 50 },
//                     borderRadius: 1,
//                     objectFit: "cover",
//                     flexShrink: 0,
//                   }}
//                 />
//                 <Box flex={1} minWidth={0}>
//                   <Typography
//                     variant="body2"
//                     sx={{
//                       fontSize: { xs: "12px", sm: "14px" },
//                       whiteSpace: "nowrap",
//                       overflow: "hidden",
//                       textOverflow: "ellipsis",
//                     }}
//                   >
//                     {item.name}
//                   </Typography>
//                   <Typography
//                     variant="caption"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "10px", sm: "11px" } }}
//                   >
//                     ₹{unitTotal.toFixed(2)} × {item.quantity}
//                   </Typography>
//                 </Box>
//               </Box>
//               <Box
//                 display="flex"
//                 alignItems="center"
//                 justifyContent="space-between"
//                 width="100%"
//                 mt={1}
//                 flexWrap="wrap"
//                 gap={1}
//               >
//                 <Box display="flex" alignItems="center" gap={0.5}>
//                   <IconButton
//                     size="small"
//                     onClick={() => updateQuantity(item.id, -1)}
//                     disabled={item.quantity <= 1}
//                   >
//                     <Remove fontSize="small" />
//                   </IconButton>
//                   <TextField
//                     value={item.quantity}
//                     onChange={(e) => handleManualQuantityChange(item.id, e.target.value)}
//                     type="number"
//                     size="small"
//                     inputProps={{
//                       min: 1,
//                       style: { width: "40px", textAlign: "center", fontSize: "12px" },
//                       inputMode: "numeric",
//                     }}
//                     sx={{
//                       "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
//                         WebkitAppearance: "none",
//                         margin: 0,
//                       },
//                       "& input[type=number]": { MozAppearance: "textfield" },
//                     }}
//                   />
//                   <IconButton size="small" onClick={() => updateQuantity(item.id, 1)}>
//                     <Add fontSize="small" />
//                   </IconButton>
//                 </Box>
//                 <Box display="flex" alignItems="center" gap={0.5}>
//                   <IconButton size="small" color="error" onClick={() => removeItem(item.id)}>
//                     <Delete fontSize="small" />
//                   </IconButton>
//                   <Typography fontSize={{ xs: "12px", sm: "13px" }} fontWeight="bold">
//                     ₹{total.toFixed(2)}
//                   </Typography>
//                 </Box>
//               </Box>
//             </ListItem>
//           );
//         })}
//         {cartItems.length === 0 && (
//           <Typography
//             variant="body2"
//             color="error"
//             textAlign="center"
//             mt={2}
//             fontSize={{ xs: "12px", sm: "14px" }}
//           >
//             No items in cart.
//           </Typography>
//         )}
//       </List>

//       {/* Fixed Footer */}
//       <Box
//         sx={{
//           position: "sticky",
//           bottom: 0,
//           bgcolor: "white",
//           p: { xs: 1, sm: 2 },
//           borderTop: "1px solid #eee",
//         }}
//       >
//         <Divider sx={{ mb: 2 }} />
//         <Typography
//           variant="body2"
//           color="text.secondary"
//           sx={{ fontSize: { xs: "12px", sm: "14px" } }}
//         >
//           Total:
//         </Typography>
//         <Typography
//           variant="h6"
//           fontWeight="bold"
//           sx={{ fontSize: { xs: "16px", sm: "18px" } }}
//         >
//           ₹{totals.total.toFixed(2)}
//         </Typography>
//         <Button
//           fullWidth
//           sx={{
//             mt: 1,
//             background: Color.primary.main,
//             color: "white",
//             fontSize: { xs: "12px", sm: "14px" },
//             py: 1,
//             "&:disabled": {
//               background: "#7e7a7aff",
//               color: "#f3f0f0ff",
//               cursor: "not-allowed",
//               pointerEvents: "auto",
//             },
//           }}
//           onClick={() => {
//             setOpenCartModal(false);
//             navigate("/order-details");
//           }}
//           disabled={cartItems.length === 0}
//         >
//           Proceed Order
//         </Button>
//       </Box>
//     </Box>
//   );

//   return (
//      <Box
//       p={{ xs: 0, sm: 0 }}
//       mt={{ xs: 2 }}
//       mb={{ xs: 0, sm: 'auto' }}
//       display="flex"
//       flexDirection={{ xs: 'column', md: 'row' }}
//       gap={{ xs: 1, sm: 2 }}
//       sx={{ fontFamily: '"Poppins", sans-serif' }}
//     >
//       <Box
//         flex={1}
//         sx={{ width: { xs: "100%", md: "70%" } }}
//         position="relative"
//       >
//         {/* Sticky Search and Category Section */}
//         <Box
//           sx={{
//             position: "sticky",
//             top: { xs: 0, md: 17 },
//             zIndex: 10,
//             backgroundColor: "white",
//             borderRadius: "6px",
//             width: '99.2%'
//           }}
//         >
//           <Box
//             display="flex"
//             flexDirection={{ xs: "column", sm: "row" }}
//             gap={{ xs: 1, sm: 2 }}
//             alignItems="center"
//             sx={{ width: "100%" }}
//             p={{ xs: 1, md: 2 }}
//           >
//             <TextField
//               fullWidth
//               variant="outlined"
//               placeholder="Search products..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <Search />
//                   </InputAdornment>
//                 ),
//               }}
//               size="small"
//               sx={{
//                 flex: 1,
//                 width: { xs: "100%", sm: "250px" },
//               }}
//             />
//             <Button
//               sx={{
//                 flexShrink: 0,
//                 minWidth: { xs: "100%", sm: "150px", md: "180px" },
//                 background: Color.primary.main,
//                 color: "white",
//                 fontSize: { xs: "12px", sm: "14px" },
//               }}
//               startIcon={<AddIcon />}
//               onClick={() => setOpenModal(true)}
//             >
//               Add Product
//             </Button>
//           </Box>

//           <Paper
//             sx={{
//               height: { xs: 48, sm: 56, md: 60 },
//               mt: { xs: 1, sm: 0 },
//               mb: { xs: 1, sm: 4 },
//               px: { xs: 1, sm: 1.5 },
//               backgroundColor: "#f9f9f9",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               overflow: "hidden",
//               borderRadius: 2,
//             }}
//             elevation={2}
//           >
//             <IconButton
//               onClick={() => handleCategoryScroll("left")}
//               disabled={isAtStart || uniqueCategories.length <= 1}
//               sx={{
//                 flexShrink: 0,
//                 p: { xs: 0.5, sm: 1 },
//                 color: Color.primary.main,
//                 "&:disabled": { color: "#ccc" },
//                 border: "1px solid gray",
//               }}
//             >
//               <ChevronLeft fontSize="small" />
//             </IconButton>
//             <Box
//               ref={categoryRef}
//               sx={{
//                 display: "flex",
//                 gap: { xs: 0.8, sm: 1.2, md: 1.2 },
//                 pl: 1,
//                 overflowX: "auto",
//                 whiteSpace: "nowrap",
//                 flexGrow: 1,
//                 py: 0.5,
//                 boxShadow: "none",
//                 scrollBehavior: "smooth",
//                 ":hover": "none",
//                 "&::-webkit-scrollbar": { display: "none" },
//                 "-ms-overflow-style": "none",
//                 "scrollbar-width": "none",
//               }}
//             >
//               {uniqueCategories.map((cat) => {
//                 const catName = cat.split(" (")[0];
//                 return (
//                   <Button
//                     key={cat}
//                     variant={category === catName ? "contained" : "outlined"}
//                     onClick={() => setCategory(catName)}
//                     sx={{
//                       flexShrink: 0,
//                       borderRadius: "20px",
//                       textTransform: "none",
//                       px: { xs: 0.8, sm: 1, md: 1.2 },
//                       py: { xs: 0.4, sm: 0.5 },
//                       fontSize: { xs: "10px", sm: "11px", md: "12px" },
//                       fontWeight: 500,
//                       minWidth: { xs: "auto", sm: "70px", md: "80px" },
//                       background: category === catName ? Color.primary.main : "white",
//                       color: category === catName ? "white" : "black",
//                       borderColor: Color.primary.main,
//                       boxShadow: "none",
//                       "&:hover": {
//                         background: category === catName ? Color.primary.dark : "#f1f1f1",
//                       },
//                     }}
//                   >
//                     {cat}
//                   </Button>
//                 );
//               })}
//             </Box>
//             <IconButton
//               onClick={() => handleCategoryScroll("right")}
//               disabled={isAtEnd || uniqueCategories.length <= 1}
//               sx={{
//                 flexShrink: 0,
//                 p: { xs: 0.5, sm: 1 },
//                 color: Color.primary.main,
//                 "&:disabled": { color: "#ccc" },
//                 border: "1px solid gray",
//               }}
//             >
//               <ChevronRight fontSize="small" />
//             </IconButton>
//           </Paper>
//         </Box>

//         {loading ? (
//           <Box display="flex" justifyContent="center" mt={5}>
//             <CircularProgress />
//           </Box>
//         ) : (
//           <>
//             <Box
//               ref={scrollRef}
//               sx={{
//                 height: { xs: "60vh", sm: "70vh", md: "80vh" },
//                 overflowY: "auto",
//                 pr: { xs: 0.5, sm: 1 },
//               }}
//             >
//               <Grid
//                 container
//                 spacing={{ xs: 1, sm: 2 }}
//                 justifyContent="flex-start"
//                 sx={{
//                   // Ensure two-per-row layout only on mobile
//                   display: { xs: "grid", sm: "flex" },
//                   gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "none" },
//                   gap: { xs: "8px", sm: 2, md: 2 },
//                 }}
//               >
//                 {filtered.slice(0, visibleCount).map((p) => {
//                   const price = parseFloat(p.base_price);
//                   const gst = parseFloat(p.tax_rate);
//                   const total = price + (price * gst) / 100;
//                   return (
//                     <Grid
//                       item
//                       xs={6} // Changed from xs={12} to ensure 2 per row on mobile
//                       sm={6}
//                       md={4}
//                       lg={3}
//                       key={p.id}
//                       display="flex"
//                       justifyContent="flex-start"
//                       mt={{ xs: 1, md: 2 }}
//                     >
//                       <Card
//                         onClick={() => addToCart(p)}
//                         sx={{
//                           width: { xs: '100%', sm: 150.7, md: 148, lg:181.7 },
//                           height: { xs: 'auto', sm: 220, md: 225, lg:230 },
//                           borderRadius: 4,
//                           boxShadow: 1,
//                           display: "flex",
//                           flexDirection: "column",
//                           justifyContent: "flex-start",
//                           p: 1,
//                           cursor: "pointer",
//                         }}
//                       >
//                         <img
//                           src={p.image_url || photo}
//                           alt={p.name}
//                           onError={(e) => {
//                             e.target.onerror = null;
//                             e.target.src = photo;
//                           }}
//                           style={{
//                             width: "100%",
//                             height: "80%",
//                             borderRadius: 10,
//                             objectFit: "cover",
//                             marginBottom: 0,
//                             aspectRatio: { xs: "1/1", sm: "auto" }, // Square images on mobile
//                           }}
//                         />
//                         <CardContent
//                           sx={{
//                             pl: 0.5,
//                             overflow: "hidden",
//                             whiteSpace: "normal",
//                             textOverflow: "ellipsis",
//                             fontFamily: '"Poppins", sans-serif',
//                           }}
//                         >
//                           <Typography
//                             variant="body2"
//                             fontWeight={400}
//                             sx={{
//                               fontSize: { xs: "11px", sm: "13px", md: "16px" },
//                               lineHeight: 1.2,
//                               color: "black",
//                               overflow: "hidden",
//                               display: "-webkit-box",
//                               WebkitLineClamp: 2,
//                               WebkitBoxOrient: "vertical",
//                               wordBreak: "break-word",
//                             }}
//                             title={p.name}
//                           >
//                             {p.name}
//                           </Typography>
//                           <Typography
//                             variant="caption"
//                             sx={{ fontSize: { xs: "10px", sm: "12px" } }}
//                           >
//                             ₹{total.toFixed(2)} <span style={{ color: "gray" }}>per piece</span>
//                           </Typography>
//                         </CardContent>
//                       </Card>
//                     </Grid>
//                   );
//                 })}
//               </Grid>
//               {scrollLoading && (
//                 <Box display="flex" justifyContent="center" py={2}>
//                   <CircularProgress size={24} />
//                 </Box>
//               )}
//               {!hasMore && !scrollLoading && (
//                 <Typography textAlign="center" py={2} color="text.secondary" fontWeight={700}>
//                   No more products to load
//                 </Typography>
//               )}
//             </Box>

//             {isMobileOrTablet && (
//               <Box
//                 sx={{
//                   position: "fixed",
//                   bottom: 150,
//                   right: 15,
//                   zIndex: 5,
//                   display: "flex",
//                   flexDirection: "column",
//                   gap: 0.5,
//                 }}
//               >
//                 <IconButton
//                   onClick={() => handleScrollButtonClick("up")}
//                   disabled={!showScrollUp}
//                   sx={{
//                     bgcolor: "white",
//                     boxShadow: 2,
//                     color: Color.primary.main,
//                     "&:hover": { bgcolor: "grey.100" },
//                     "&:disabled": { bgcolor: "grey.200", color: "grey.500" },
//                   }}
//                   size="small"
//                 >
//                   <KeyboardArrowUp />
//                 </IconButton>
//                 <IconButton
//                   onClick={() => handleScrollButtonClick("down")}
//                   disabled={!showScrollDown}
//                   sx={{
//                     bgcolor: "white",
//                     boxShadow: 2,
//                     color: Color.primary.main,
//                     "&:hover": { bgcolor: "grey.100" },
//                     "&:disabled": { bgcolor: "grey.200", color: "grey.500" },
//                   }}
//                   size="small"
//                 >
//                   <KeyboardArrowDown />
//                 </IconButton>
//               </Box>
//             )}

//             {/* View Cart Button for Mobile/Tablet with Item Count */}
//             {isMobileOrTablet && (
//               <Fab
//                 color="primary"
//                 onClick={() => setOpenCartModal(true)}
//                 sx={{
//                   position: "fixed",
//                   bottom: 80,
//                   right: 20,
//                   zIndex: 10,
//                   background: Color.primary.main,
//                   "&:hover": {
//                     background: Color.primary.main,
//                   },
//                   "&:active": {
//                     background: Color.primary.main,
//                   },
//                   "&:focus": {
//                     background: Color.primary.main,
//                   },
//                 }}
//               >
//                 <Badge badgeContent={totalItems} color='secondary' size='medium' width={'40px'}>
//                   <ShoppingCart />
//                 </Badge>
//               </Fab>
//             )}
//           </>
//         )}
//       </Box>

//       {/* Cart Modal for Mobile/Tablet */}
//       {isMobileOrTablet && (
//         <Modal
//           open={openCartModal}
//           onClose={() => setOpenCartModal(false)}
//           sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
//         >
//           <Box
//             sx={{
//               width: { xs: "90%", sm: "80%" },
//               maxWidth: 400,
//               bgcolor: "white",
//               borderRadius: 2,
//               overflow: "hidden",
//             }}
//           >
//             {renderCartDetails()}
//           </Box>
//         </Modal>
//       )}

//       {/* Desktop Cart Panel with Fixed Height */}
//       {!isMobileOrTablet && (
//         <Box
//           width={{ xs: "100%", sm: "100%", md: "25%", lg: "25%" }}
//           minWidth={{ xs: "auto", md: "300px" }}
//           height="765px"
//           sx={{
//             position: { md: "sticky", xs: "static" },
//             top: { md: 15, lg:17 },
//           }}
//         >
//           {renderCartDetails()}
//         </Box>
//       )}

//       <AddProduct open={openModal} onClose={() => setOpenModal(false)} />
//     </Box>
//   );
// };

// export default CreateOrder;